/**
 * useBackgroundWs — Manages the Android Foreground Service for WS notifications.
 *
 * When the user is authenticated on an Android device with an on-prem backend,
 * starts a foreground service that maintains a persistent WS connection.
 * The service shows system notifications even when screen is locked.
 *
 * When the WebView is active, the web-layer WS hook (useNotificationWebSocket)
 * handles real-time updates and cache invalidation. The foreground service
 * is the fallback for when the WebView is suspended (screen locked, app bg'd).
 */

import { useEffect, useRef } from 'react';
import { isNativePlatform, getPlatform } from '../native-features';
import { getSessionToken, getDeviceToken } from '../better-auth';
import { BackgroundWs, getBackgroundWsUrl } from '../background-ws-plugin';

interface UseBackgroundWsOptions {
  userId: string | undefined;
  token: string | null | undefined;
  enabled: boolean;
}

export function useBackgroundWs(options: UseBackgroundWsOptions) {
  const { userId, token, enabled } = options;
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isNativePlatform() || getPlatform() !== 'android') return;
    if (!enabled || !userId) return;

    const wsUrl = getBackgroundWsUrl();
    if (!wsUrl) return; // Cloud deployment — no background WS

    // Use session token, fallback to device token
    const authToken = token || getSessionToken() || getDeviceToken();
    if (!authToken) return;

    let cancelled = false;

    (async () => {
      try {
        // Check if already running with same user
        const { running } = await BackgroundWs.isRunning();
        if (cancelled) return;

        if (running) {
          // Update config (token may have refreshed)
          await BackgroundWs.updateConfig({ authToken, wsUrl, userId });
          startedRef.current = true;
          return;
        }

        await BackgroundWs.start({ wsUrl, authToken, userId });
        startedRef.current = true;
        console.log('[BgWs] Foreground service started');
      } catch (err) {
        console.warn('[BgWs] Failed to start service:', err);
      }
    })();

    return () => {
      cancelled = true;
      // Don't stop service on unmount — it should persist across navigation.
      // Service is stopped on logout (see stopBackgroundService).
    };
  }, [enabled, userId, token]);

  return {
    /** Programmatically stop the background service (call on logout) */
    stop: async () => {
      try {
        await BackgroundWs.stop();
        startedRef.current = false;
        console.log('[BgWs] Foreground service stopped');
      } catch (err) {
        console.warn('[BgWs] Failed to stop service:', err);
      }
    },
  };
}

/** Stop the background service (call from logout flow). Exported for direct use. */
export async function stopBackgroundService(): Promise<void> {
  if (!isNativePlatform() || getPlatform() !== 'android') return;
  try {
    const { BackgroundWs } = await import('../background-ws-plugin');
    await BackgroundWs.stop();
    console.log('[BgWs] Service stopped (logout)');
  } catch (err) {
    console.warn('[BgWs] Failed to stop on logout:', err);
  }
}
